import React, { useState } from "react";
import styles from "./OrderLookup.module.css";
import { getOrderByGeneratedId } from "../../lib/getOrderByGeneratedId";
import ProductImage from "../../components/common/ProductImage/ProductImage";
import {
  UnifiedOrderData,
  transformLegacyToUnified,
  hasCleanPricing,
  CleanOrderProduct,
  CleanOrderGroup,
} from "../../types/orderTypes";

// Legacy interface - for backward compatibility during migration
interface LegacyProduct {
  id: number;
  quantity: number;
  price: number;
  finalPrice: number;
  product: {
    id: number;
    name: string;
    images: {
      thumbnail: string;
      small: string;
      medium: string | null;
    }[];
  };
}

interface LegacyGroup {
  id: number;
  deliveryMethod: string;
  deliveryDate: string;
  subTotal: number;
  deliveryFee: number;
  groupTotal: number;
  items: LegacyProduct[];
}

interface LegacyOrderDataLocal {
  orderId: string;
  createdAt: string;
  orderDate: string;
  generatedOrderId: string;
  customerName: string;
  totalPrice: number;
  totalDeliveryFee: number;
  finalTotal: number;
  paymentMethod: string;
  groups: LegacyGroup[];
  orderStatus: "processing" | "shipped" | "delivered";
  paymentStatus: "pending" | "paid";
}

const steps = [
  "Pendiente de Pago",
  "Confirmado",
  "Preparando",
  "En camino",
  "Entregado",
];

const OrderLookup: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [generatedOrderId, setGeneratedOrderId] = useState<string>("");
  const [orderData, setOrderData] = useState<UnifiedOrderData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // 🔧 FIX #3: Clean and prepare order ID for search
    const cleanedOrderId = generatedOrderId.trim();

    console.log("email", email);
    console.log("generatedOrderId (original):", generatedOrderId);
    console.log("generatedOrderId (cleaned):", cleanedOrderId);

    try {
      const response = await getOrderByGeneratedId(email, cleanedOrderId);
      if (response?.orderData) {
        console.log(
          "🔍 RAW ORDER RESPONSE:",
          JSON.stringify(response, null, 2)
        );
        console.log(
          "🔍 ORDER DATA RECEIVED:",
          JSON.stringify(response.orderData, null, 2)
        );

        // Check if this is already clean pricing structure
        const hasCleanPricingStructure = hasCleanPricing(response.orderData);
        console.log(
          "🔍 HAS CLEAN PRICING STRUCTURE:",
          hasCleanPricingStructure
        );

        // Log the structure we're dealing with
        if (hasCleanPricingStructure) {
          console.log("🔍 CLEAN PRICING TOTALS:", response.orderData.totals);
          console.log("🔍 APPLIED COUPONS:", response.orderData.appliedCoupons);
          console.log(
            "🔍 APPLIED PROMOTIONS:",
            response.orderData.appliedPromotions
          );
        }

        // Transform to unified format if needed
        const rawOrderData = response.orderData as
          | LegacyOrderDataLocal
          | UnifiedOrderData;
        const unifiedOrderData = hasCleanPricingStructure
          ? (rawOrderData as UnifiedOrderData)
          : transformLegacyToUnified(rawOrderData as LegacyOrderDataLocal);

        setOrderData(unifiedOrderData);
        console.log("Unified Order Data:", JSON.stringify(unifiedOrderData));
      } else {
        setError(
          response?.message ||
            "Pedido no encontrado. Verifica que el número de orden y correo electrónico sean correctos."
        );
      }
    } catch (err) {
      console.error("Error fetching order:", err);
      setError("Ocurrió un error inesperado. Por favor, inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const getActiveStepIndex = (order: UnifiedOrderData) => {
    if (order.paymentStatus === "pending") return 0;
    if (order.paymentStatus === "paid") return 1;
    if (order.orderStatus === "shipped") return 3;
    if (order.orderStatus === "delivered") return 4;
    return 2; // "Preparando" is default after paid
  };

  const renderStepper = (order: UnifiedOrderData) => {
    const activeStepIndex = getActiveStepIndex(order);

    return (
      <div className={styles.stepper}>
        {steps.map((step, index) => {
          const isCompleted = index < activeStepIndex;
          const isActive = index === activeStepIndex;

          return (
            <div key={index} className={styles.step}>
              <div
                className={`${styles.circle} ${
                  isCompleted ? styles.completed : ""
                } ${isActive ? styles.active : ""}`}
              >
                {index + 1}
              </div>
              <div className={styles.stepLabel}>{step}</div>
              {index !== steps.length - 1 && (
                <div
                  className={`${styles.connector} ${
                    isCompleted ? styles.completedConnector : ""
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const formatDeliveryMethod = (method: string) => {
    switch (method.toLowerCase()) {
      case "standard":
        return "Standard";
      case "rapido":
        return "Rápido";
      case "express":
        return "Express";
      default:
        return method;
    }
  };

  const formatDate = (date: string) => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    };
    return new Date(date).toLocaleDateString("es-ES", options);
  };

  const renderGroupProduct = (product: CleanOrderProduct) => {
    // FIXED: Use stored historical pricing - NO recalculation
    const unitPrice = product.pricing?.originalUnitPrice || product.price || 0;
    const totalPrice = product.pricing?.finalTotal || product.finalPrice || 0;
    const hasDiscount =
      (product.pricing?.discounts?.promotional &&
        product.pricing.discounts.promotional.totalAmount > 0) ||
      (product.pricing?.discounts?.coupon &&
        product.pricing.discounts.coupon.totalAmount > 0);

    return (
      <div key={product.id} className={styles.product}>
        <ProductImage
          src={`${product.product.images[0]?.thumbnail}`}
          alt={product.product.name}
          className={styles.productImage}
        />
        <div className={styles.productInfo}>
          <span className={styles.productName}>{product.product.name}</span>
          <div className={styles.productPricing}>
            {/* FIXED: Display stored historical data only */}
            <span className={styles.productDetails}>
              {product.quantity} x S/ {unitPrice.toFixed(2)}
              {hasDiscount && (
                <span className={styles.discountIndicator}>
                  {" "}
                  (con descuento aplicado)
                </span>
              )}
            </span>
            <span className={styles.productTotal}>
              S/ {totalPrice.toFixed(2)}
            </span>
          </div>

          {/* Display stored discount information */}
          {hasDiscount && (
            <div className={styles.discountDetails}>
              {product.pricing?.discounts?.promotional &&
                product.pricing.discounts.promotional.totalAmount > 0 && (
                  <span className={styles.discountAmount}>
                    Dcto Promo: -S/{" "}
                    {product.pricing.discounts.promotional.totalAmount.toFixed(
                      2
                    )}
                  </span>
                )}
              {product.pricing?.discounts?.coupon &&
                product.pricing.discounts.coupon.totalAmount > 0 && (
                  <span className={styles.discountAmount}>
                    Cupón ({product.pricing.discounts.coupon.code}): -S/{" "}
                    {product.pricing.discounts.coupon.totalAmount.toFixed(2)}
                  </span>
                )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderGroup = (group: CleanOrderGroup, index: number) => {
    // FIXED: Use stored historical group totals - NO recalculation
    const originalSubtotal =
      group.groupTotals?.originalSubtotal || group.subTotal || 0;
    const finalSubtotal =
      group.groupTotals?.finalSubtotal || group.subTotal || 0;
    const deliveryFee =
      group.groupTotals?.deliveryFeeTotal || group.deliveryFee || 0;
    const groupTotal = group.groupTotals?.groupTotal || group.groupTotal || 0;

    return (
      <div key={group.id} className={styles.cartGroup}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h2>{`Paquete ${index + 1} - ${formatDeliveryMethod(
              group.deliveryMethod
            )} - ${formatDate(group.deliveryDate)}`}</h2>
          </div>
        </div>

        <div className={styles.items}>
          {group.items.map(renderGroupProduct)}
        </div>

        <div className={styles.footer}>
          <div className={styles.footerContent}>
            {/* FIXED: Display stored historical breakdown */}
            <div className={styles.footerRow}>
              <span>Subtotal original:</span>
              <span>S/ {originalSubtotal.toFixed(2)}</span>
            </div>

            {(group.groupTotals?.promotionalDiscountTotal || 0) > 0 && (
              <div className={styles.footerRow}>
                <span>Dcto Promo:</span>
                <span className={styles.discountAmount}>
                  -S/{" "}
                  {(group.groupTotals.promotionalDiscountTotal || 0).toFixed(2)}
                </span>
              </div>
            )}

            {(group.groupTotals?.couponDiscountTotal || 0) > 0 && (
              <div className={styles.footerRow}>
                <span>Descuento con cupón:</span>
                <span className={styles.discountAmount}>
                  -S/ {(group.groupTotals.couponDiscountTotal || 0).toFixed(2)}
                </span>
              </div>
            )}

            <div className={styles.footerRow}>
              <span>Subtotal:</span>
              <span>S/ {finalSubtotal.toFixed(2)}</span>
            </div>
            <div className={styles.footerRow}>
              <span>Costo de Envío:</span>
              <span>S/ {deliveryFee.toFixed(2)}</span>
            </div>
            <div className={styles.footerRow}>
              <span>
                <strong>Total del Paquete:</strong>
              </span>
              <span>
                <strong>S/ {groupTotal.toFixed(2)}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // **NEW: Helper function to format complete address**
  const formatCompleteAddress = (address: any): string => {
    if (!address) return "No especificado";

    const addressParts = [];

    // Add street and street number
    if (address.street) {
      let streetInfo = address.street;
      if (address.streetNumber) {
        streetInfo += ` ${address.streetNumber}`;
      }
      addressParts.push(streetInfo);
    }

    // Add district
    if (address.district) {
      addressParts.push(address.district);
    }

    // Add province (if different from district)
    if (address.province && address.province !== address.district) {
      addressParts.push(address.province);
    }

    // Add department
    if (address.department) {
      addressParts.push(address.department);
    }

    // Add country
    if (address.country) {
      addressParts.push(address.country);
    }

    return addressParts.length > 0
      ? addressParts.join(", ")
      : "No especificado";
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Sigue tu pedido</h1>

      <div className={styles.searchSection}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.inputGroup}>
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Ingresa tu correo electrónico"
            />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="generatedOrderId">Número de Orden</label>
            <input
              type="text"
              id="generatedOrderId"
              value={generatedOrderId}
              onChange={(e) => setGeneratedOrderId(e.target.value)}
              required
              placeholder="Ej: 108 o 0000000108"
            />
            <small
              style={{
                color: "#666",
                fontSize: "0.85em",
                marginTop: "4px",
                display: "block",
              }}
            >
              Puedes usar el número completo (0000000108) o solo los últimos
              dígitos (108)
            </small>
          </div>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? "Buscando..." : "Buscar"}
          </button>
        </form>
      </div>

      {error && <p className={styles.error}>{error}</p>}

      {orderData && (
        <>
          {renderStepper(orderData)}

          <div className={styles.orderInfo}>
            <div className={styles.card}>
              <h2>Información de la Orden</h2>
              <div className={styles.infoGrid}>
                <p>
                  <strong>Orden #{orderData.generatedOrderId}</strong>
                </p>
                <p>
                  <strong>Cliente:</strong> {orderData.customerName}
                </p>
                <p>
                  <strong>Método de pago:</strong>{" "}
                  {orderData.paymentMethod === "money_transfer"
                    ? "Transferencia Bancaria"
                    : orderData.paymentMethod === "yapePlin"
                    ? "Yape / Plin"
                    : orderData.paymentMethod}
                </p>
                <p>
                  <span
                    className={`${styles.statusBadge} ${
                      orderData.paymentStatus === "paid"
                        ? styles.statusPaid
                        : styles.statusPending
                    }`}
                  >
                    {orderData.paymentStatus === "paid"
                      ? "Pagado"
                      : "Pendiente"}
                  </span>
                </p>
              </div>
            </div>

            {/* **NEW: Shipping Information Card** */}
            <div className={styles.card}>
              <h2>Información de Envío</h2>
              <div className={styles.shippingInfo}>
                <p>
                  <strong>Destinatario:</strong>{" "}
                  {orderData.receiverInfo?.name || orderData.customerName}
                </p>
                <p>
                  <strong>Teléfono:</strong>{" "}
                  {orderData.receiverInfo?.phone ||
                    orderData.customerInfo?.phone ||
                    "No especificado"}
                </p>
                {/* **FIXED: Single Dirección field with complete address** */}
                <p>
                  <strong>Dirección:</strong>{" "}
                  {formatCompleteAddress(orderData.user_address)}
                </p>
                {/* **NEW: Reference as separate field if available** */}
                {orderData.user_address?.reference && (
                  <p>
                    <strong>Referencia:</strong>{" "}
                    {orderData.user_address.reference}
                  </p>
                )}
              </div>
            </div>

            <div className={styles.orderSummary}>
              <h2>Detalles de la Orden</h2>
              <div className={styles.groupsContainer}>
                {orderData.groups.map((group, index) =>
                  renderGroup(group, index)
                )}
              </div>

              {/* FIXED: Use OrderComplete styling structure with orderTotals wrapper */}
              <div className={styles.orderTotals}>
                <div className={styles.totalRow}>
                  <span>Subtotal de Productos:</span>
                  <span>S/ {orderData.totals.originalSubtotal.toFixed(2)}</span>
                </div>

                {orderData.totals.promotionalDiscountTotal > 0 && (
                  <div className={styles.totalRow}>
                    <span>Descuentos Promocionales:</span>
                    <span className={styles.discountAmount}>
                      -S/ {orderData.totals.promotionalDiscountTotal.toFixed(2)}
                    </span>
                  </div>
                )}

                {orderData.totals.couponDiscountTotal > 0 && (
                  <div className={styles.totalRow}>
                    <span>Descuentos por Cupón:</span>
                    <span className={styles.discountAmount}>
                      -S/ {orderData.totals.couponDiscountTotal.toFixed(2)}
                    </span>
                  </div>
                )}

                <div className={styles.totalRow}>
                  <span>Subtotal Final:</span>
                  <span>S/ {orderData.totals.finalSubtotal.toFixed(2)}</span>
                </div>
                <div className={styles.totalRow}>
                  <span>Total de Envío:</span>
                  <span>S/ {orderData.totals.deliveryFeeTotal.toFixed(2)}</span>
                </div>
                <div className={`${styles.totalRow} ${styles.finalTotal}`}>
                  <span>
                    <strong>Total de la Orden:</strong>
                  </span>
                  <span>
                    <strong>S/ {orderData.totals.grandTotal.toFixed(2)}</strong>
                  </span>
                </div>
              </div>

              {/* **MOVED: Display stored applied coupons and promotions as separate detail section** */}
              {(orderData.appliedCoupons.length > 0 ||
                orderData.appliedPromotions.length > 0) && (
                <div className={styles.promotionsDetailSection}>
                  <h3>Detalle de Descuentos Aplicados</h3>

                  {orderData.appliedCoupons.length > 0 && (
                    <div className={styles.appliedCoupons}>
                      <h4>Cupones aplicados:</h4>
                      {orderData.appliedCoupons.map((coupon, idx) => (
                        <div key={idx} className={styles.promotionDetailItem}>
                          <span>
                            {coupon.code} - {coupon.description}
                          </span>
                          <span className={styles.discountAmount}>
                            -S/ {coupon.totalDiscount.toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {orderData.appliedPromotions.length > 0 && (
                    <div className={styles.appliedPromotions}>
                      <h4>Promociones aplicadas:</h4>
                      {orderData.appliedPromotions.map((promotion, idx) => (
                        <div key={idx} className={styles.promotionDetailItem}>
                          <span>Descuento Promocional:</span>
                          <span className={styles.discountAmount}>
                            -S/ {promotion.totalDiscount.toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrderLookup;
